import { Text, useDisclosure } from '@chakra-ui/react';
import { CreditCardIcon } from '@heroicons/react/outline';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { Exceptions, SubscriptionStepStatus, noop } from '@blockpulse3/data/shared';
import {
  FundraisingDocumentType,
  MergedSubscriptionDetailsStep,
  useGetSubscriptionQuery,
  useValidateSubscriptionDetailsMutation,
} from '@blockpulse3/graphql/hooks';
import { formatNumberCurrency } from '@blockpulse3/helpers';
import { useErrorToast } from '@blockpulse3/ui/commons';

import { SubscriptionStep } from '../../SubscriptionStep';
import { SubscriptionDetailsForm, SubscriptionDetailsModal } from './SubscriptionDetailsModal';

type HoldingMethodTypeMessage = keyof Messages['HoldingMethodTypeValue'];

type Props = {
  subscriptionStep: MergedSubscriptionDetailsStep;
  isDisabled: boolean;
};

export function SubscriptionDetailsStep({ subscriptionStep, isDisabled }: Props): JSX.Element {
  const i18nSubscription = useTranslations('Subscription');
  const i18nHoldingMethodTypeValue = useTranslations('HoldingMethodTypeValue');

  const { isOpen, onClose, onOpen } = useDisclosure();
  const errorToast = useErrorToast();

  const { subscriptionId = '' } = useParams();

  const [validateSubscriptionDetails] = useValidateSubscriptionDetailsMutation();

  const { data, refetch } = useGetSubscriptionQuery({
    variables: { subscriptionId },
  });
  const subscription = data?.subscription;
  const holdingMethod = subscription?.holdingMethod;

  const investAmount = subscription?.investAmount;
  const isInvestAmount = !!investAmount && investAmount !== 0;

  const subscriptionDocument = subscription?.data.mergedSteps?.find(
    (step) =>
      step.__typename === 'MergedDocumentSignatureStep' &&
      step.documentType === FundraisingDocumentType.SUBSCRIPTION,
  );
  const isSubscriptionDocumentGenerated =
    subscriptionDocument && subscriptionDocument.status !== SubscriptionStepStatus.NONE;

  const handleModalOpen = (): void => {
    // Prevent modal from opening once subscription document is generated
    if (!isSubscriptionDocumentGenerated) {
      onOpen();
    }
  };

  const handleModalSubmit = (data: SubscriptionDetailsForm): void => {
    if (!subscriptionId) return;

    validateSubscriptionDetails({
      variables: {
        validateSubscriptionDetailsInput: {
          subscriptionId,
          investAmount: data.investAmount,
        },
      },
      onCompleted: ({ validateSubscriptionDetails }) => {
        refetch();

        // Only close modal if step is validated
        if (validateSubscriptionDetails) {
          onClose();
        }
      },
      onError: (err: unknown) => {
        const error = err as Error;
        if (error.message === Exceptions.ExceedsHardCap) {
          errorToast({
            title: i18nSubscription('Toast.ExceedsHardCapError'),
          });
        } else {
          errorToast({ title: i18nSubscription('Toast.AmountError') });
        }
      },
    });
  };

  const mifidStep = subscription?.data.mergedSteps.find(
    (step) => step.__typename === 'MergedMifidStep',
  );
  const subscriptionDetailsStep = subscription?.data.mergedSteps.find(
    (step) => step.__typename === 'MergedSubscriptionDetailsStep',
  );
  const proofOfFundsSourceThreshold = subscriptionStep.proofOfFundsSourceThreshold;
  const needsFundsSourceCertificate =
    !!mifidStep &&
    proofOfFundsSourceThreshold &&
    !!investAmount &&
    investAmount >= proofOfFundsSourceThreshold &&
    subscriptionDetailsStep?.status !== SubscriptionStepStatus.VALID;

  return (
    <>
      <SubscriptionStep
        icon={CreditCardIcon}
        isDisabled={isDisabled}
        status={subscriptionStep.status}
        title={i18nSubscription('SupportSelectionWithAmount')}
        type={subscriptionStep.type}
        onClick={!isDisabled ? handleModalOpen : noop}
      >
        {isInvestAmount && (
          <Text fontSize="sm">
            {i18nSubscription.rich('InvestAmountSelected', {
              investAmount: formatNumberCurrency(investAmount),
              holdingMethod: i18nHoldingMethodTypeValue(holdingMethod as HoldingMethodTypeMessage),
              important: (chunks) => <Text as="b">{chunks}</Text>,
            })}
          </Text>
        )}
        {needsFundsSourceCertificate && (
          <Text color="yellow.700" fontSize="sm">
            {i18nSubscription('FundsSourceCertificateRequired')}
          </Text>
        )}
      </SubscriptionStep>
      <SubscriptionDetailsModal
        isOpen={isOpen}
        subscriptionStep={subscriptionStep}
        onClose={onClose}
        onSubmit={handleModalSubmit}
      />
    </>
  );
}
